<template>
  <div data-testid="presence-panel" class="flex flex-col gap-6 p-4">
    <section class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 class="text-base font-medium text-gray-900">{{ $t("presencePanel.currentState") }}</h4>
          <p class="text-sm text-gray-500">{{ $t("presencePanel.intro") }}</p>
        </div>
        <button
          type="button"
          data-testid="presence-refresh"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          @click="load"
        >
          <RefreshIcon :class="[loading ? 'animate-reverse-spin' : '', 'h-4 w-4']" aria-hidden="true" />
          {{ $t("presencePanel.refresh") }}
        </button>
      </div>

      <div v-if="loading && !presence" data-testid="presence-loading" class="py-16 text-center">
        <div
          class="inline-block w-6 h-6 border-4 border-blue-500 border-solid rounded-full animate-spin border-r-transparent"
        ></div>
      </div>

      <p v-else-if="failed" data-testid="presence-error" class="py-16 text-sm text-center text-gray-500">
        {{ $t("presencePanel.error") }}
      </p>

      <div v-else-if="presence" data-testid="presence-state" class="px-4 py-3 rounded-lg shadow-md state_unknown">
        <template v-if="presence.state">
          <div class="flex flex-wrap items-center gap-2">
            <span
              :class="[badgeClass(presence.state.connected), 'px-2 py-0.5 text-xs font-medium rounded-full']"
              data-testid="presence-state-badge"
            >
              {{ $t(presence.state.connected ? "presencePanel.connected" : "presencePanel.disconnected") }}
            </span>
            <span class="text-sm">
              {{ $t("presencePanel.since") }}
              <time :title="utc(presence.state.since)">{{ local(presence.state.since) }}</time>
            </span>
          </div>
          <dl class="grid grid-cols-2 mt-3 text-sm gap-x-6 gap-y-2 md:grid-cols-5">
            <div>
              <dt class="text-gray-500">{{ $t("presencePanel.session") }}</dt>
              <dd class="font-mono text-xs break-all">{{ presence.state.sessionIdentifier || "-" }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">{{ $t("presencePanel.disconnectReason") }}</dt>
              <dd class="font-mono text-xs break-all">{{ presence.state.disconnectReason || "-" }}</dd>
            </div>
            <div>
              <dt class="text-gray-500">{{ $t("presencePanel.escalationStage") }}</dt>
              <dd
                v-if="!presence.state.connected"
                data-testid="presence-state-stage"
                :title="presence.state.escalationStage"
              >
                {{ $t(`presencePanel.stage.${presence.state.escalationStage}`) }}
              </dd>
              <dd v-else data-testid="presence-state-stage">-</dd>
            </div>
            <div>
              <dt class="text-gray-500">{{ $t("presencePanel.nextEscalation") }}</dt>
              <dd>
                <time v-if="presence.state.nextEscalationAt" :title="utc(presence.state.nextEscalationAt)">{{
                  local(presence.state.nextEscalationAt)
                }}</time>
                <span v-else>-</span>
              </dd>
            </div>
            <div>
              <dt class="text-gray-500">{{ $t("presencePanel.revision") }}</dt>
              <dd>{{ presence.state.revision }}</dd>
            </div>
          </dl>
        </template>
        <p v-else data-testid="presence-no-state" class="text-sm">{{ $t("presencePanel.noState") }}</p>
      </div>
    </section>

    <section v-if="presence && !failed" class="flex flex-col gap-3">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <h4 class="text-base font-medium text-gray-900">
          {{ $t("presencePanel.events", { count: presence.events.length }) }}
        </h4>
        <ul
          v-if="markedSources.length"
          class="flex flex-wrap gap-3 text-xs text-gray-600"
          data-testid="presence-legend"
        >
          <li v-for="source in markedSources" :key="source" class="flex items-center gap-1.5">
            <span :class="[SOURCE_STYLES[source].swatch, 'w-3 h-3 rounded-sm']" aria-hidden="true"></span>
            {{ $t(`presencePanel.sourceHint.${source}`) }}
          </li>
        </ul>
      </div>

      <p v-if="!presence.events.length" data-testid="presence-empty" class="py-8 text-sm text-center text-gray-500">
        {{ $t("presencePanel.empty") }}
      </p>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm text-left divide-y divide-gray-200">
          <thead class="text-xs text-gray-500">
            <tr>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.time") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.event") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.source") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.reason") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.clientInitiated") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.session") }}</th>
              <th scope="col" class="px-3 py-2 font-medium">{{ $t("presencePanel.column.version") }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr
              v-for="event in presence.events"
              :key="`${event.timestamp}#${event.eventType}#${event.sessionIdentifier || ''}`"
              :class="SOURCE_STYLES[event.source]?.row"
              :data-source="event.source"
              data-testid="presence-event"
            >
              <td class="px-3 py-2 whitespace-nowrap">
                <time :title="utc(event.timestamp)">{{ local(event.timestamp) }}</time>
              </td>
              <td class="px-3 py-2">
                <span
                  :class="[badgeClass(event.eventType === 'connected'), 'px-2 py-0.5 text-xs font-medium rounded-full']"
                  >{{ $t(`presencePanel.${event.eventType}`) }}</span
                >
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  :class="[SOURCE_STYLES[event.source]?.badge || 'text-gray-600', 'px-2 py-0.5 text-xs rounded-full']"
                  :title="event.source"
                  >{{ $t(`presencePanel.source.${event.source}`) }}</span
                >
              </td>
              <td class="px-3 py-2 font-mono text-xs">{{ event.disconnectReason || "-" }}</td>
              <td class="px-3 py-2">{{ yesNo(event.clientInitiatedDisconnect) }}</td>
              <td class="px-3 py-2 font-mono text-xs" :title="event.sessionIdentifier || undefined">
                {{ shorten(event.sessionIdentifier) }}
              </td>
              <td class="px-3 py-2">{{ event.versionNumber ?? "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { RefreshIcon } from "@heroicons/vue/outline";

// Events that did not come live from AWS IoT are marked, so corrections stand out.
const SOURCE_STYLES = {
  reconciler: { row: "bg-yellow-50", badge: "bg-yellow-100 text-yellow-800", swatch: "bg-yellow-200" },
  timestream_backfill: { row: "bg-sky-50", badge: "bg-sky-100 text-sky-800", swatch: "bg-sky-200" },
};

const utc = (iso) => iso.replace("T", " ").replace("+00:00", " UTC");

export default {
  name: "DevicePresencePanel",
  components: { RefreshIcon },
  props: {
    deviceId: { type: String, required: true },
  },
  setup(props) {
    const store = useStore();
    const { t, locale } = useI18n();
    const presence = ref(null);
    const loading = ref(false);
    const failed = ref(false);

    const load = async () => {
      loading.value = true;
      try {
        const result = await store.dispatch("device/getDevicePresence", { deviceId: props.deviceId });
        failed.value = !result;
        presence.value = result || null;
      } finally {
        loading.value = false;
      }
    };

    onMounted(load);

    const markedSources = computed(() =>
      Object.keys(SOURCE_STYLES).filter((source) => presence.value?.events.some((event) => event.source === source))
    );

    const local = (iso) =>
      new Intl.DateTimeFormat(locale.value, { dateStyle: "short", timeStyle: "medium" }).format(new Date(iso));
    const shorten = (value) => (!value ? "-" : value.length > 12 ? `${value.slice(0, 8)}…` : value);
    const yesNo = (value) =>
      value === null || value === undefined ? "-" : t(value ? "presencePanel.yes" : "presencePanel.no");
    const badgeClass = (connected) => (connected ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800");

    return { presence, loading, failed, load, markedSources, local, utc, shorten, yesNo, badgeClass, SOURCE_STYLES };
  },
};
</script>
